"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import { cn } from "@/lib/utils";
import {
  Home,
  Search,
  Heart,
  Menu,
  User,
  Plus,
  LayoutDashboard,
  LogOut,
  Settings,
  Briefcase,
  MessageCircle
} from "lucide-react";
import { useRightTabs } from "@/contexts/right-tabs-context";
import { useMobileToolbar, type MobileToolbarAction } from "@/contexts/mobile-toolbar-context";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BugReportDialog } from "@/components/bug-report/bug-report-dialog";
import { Bug, HelpCircle } from "lucide-react";
import { useAuthModal } from "@/contexts/auth-modal-context";
import { toast } from "sonner";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  activeIcon?: React.ComponentType<{ className?: string }>;
  badge?: number;
  action?: () => void;
  tourId?: string; // For targeting in tours
  requiresAuth?: boolean;
}

/**
 * MobileBottomNav - Primary navigation for mobile devices
 *
 * A clean bottom navigation bar following iOS/Android patterns:
 * - Fixed to bottom of screen
 * - 5 primary actions: Home, Search, Saved, Messages, More
 * - Thumb-friendly touch targets (minimum 44px)
 * - Shows active state for current route
 * - Only visible on mobile (below lg breakpoint)
 * - Supports dynamic action registration from pages/components
 *
 * UX Considerations:
 * - Pages can register custom actions via useMobileToolbar()
 * - Dynamic actions are shown first (up to 3), then static nav
 * - Badge indicators for unread messages and saved items
 * - Subtle haptic-like feedback animations
 * - Safe area padding for notched devices
 * - Height is measured and exposed for content padding
 *
 * @author Ellis Reed
 */
export function MobileBottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();
  const { liked, setMobileSheetOpen } = useRightTabs();
  const { registeredActions, setToolbarHeight } = useMobileToolbar();
  const { openAuthModal } = useAuthModal();
  const navRef = React.useRef<HTMLDivElement>(null);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const [measuredHeight, setMeasuredHeight] = React.useState(64);

  // Fix hydration mismatch by only rendering badges after mount
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Measure toolbar height and expose via context
  React.useEffect(() => {
    if (!navRef.current) return;

    const measureHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        setMeasuredHeight(height);
        setToolbarHeight(height);
      }
    };

    // Initial measurement
    measureHeight();

    // Re-measure on resize (orientation change, etc.)
    const resizeObserver = new ResizeObserver(measureHeight);
    resizeObserver.observe(navRef.current);

    return () => resizeObserver.disconnect();
  }, [setToolbarHeight]);

  // Hide mobile bottom nav on admin pages (admin has its own layout)
  const isAdminPage = pathname?.startsWith("/admin");
  if (isAdminPage) {
    return null;
  }

  const isAuthenticated = status === "authenticated";
  const likedCount = liked.length;

  const handleLogout = async () => {
    try {
      await signOut({ redirect: false });
      setMoreMenuOpen(false);
      toast.success("Logged out successfully");
      router.push("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out");
    }
  };

  // Navigation items for bottom bar
  const navItems: NavItem[] = [
    {
      title: "Home",
      href: "/",
      icon: Home,
      tourId: "mobile-nav-home",
    },
    {
      title: "Search",
      href: "/search",
      icon: Search,
      tourId: "mobile-nav-search",
    },
    {
      title: "Saved",
      href: "#",
      icon: Heart,
      action: () => setMobileSheetOpen(true),
      tourId: "mobile-nav-saved",
    },
    {
      title: "Messages",
      href: "/messages",
      icon: MessageCircle,
      tourId: "mobile-nav-messages",
    },
    {
      title: "Profile",
      href: "/user/profile",
      icon: User,
      tourId: "mobile-nav-profile",
    },
    {
      title: "More",
      href: "#",
      icon: Menu,
      action: () => setMoreMenuOpen(true),
      tourId: "mobile-nav-more",
    },
  ];

  // More menu items (Dashboard removed since it's now in bottom nav)
  const moreMenuItems = [
    { title: "Saved", href: "#", icon: Heart, requiresAuth: true, action: () => setMobileSheetOpen(true) },
    { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, requiresAuth: true },
    { title: "Profile", href: "/user/profile", icon: User, requiresAuth: true },
    { title: "My Listings", href: "/user/listings", icon: Plus, requiresAuth: true },
    { title: "Saved Searches", href: "/user/saved-searches", icon: Search, requiresAuth: true },
  ];

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If already on home page, scroll to top instead of navigating
    if (pathname === "/" || pathname === "/home") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav
        ref={navRef}
        data-tour="mobile-nav"
        className={cn(
          // Only show on mobile/tablet (below lg breakpoint)
          "lg:hidden",
          // Fixed positioning at bottom
          // Below modals (--z-modal-backdrop: 1050) but above fixed content (--z-fixed: 1030)
          "fixed bottom-0 left-0 right-0 z-[1040]",
          // Border and shadow
          "border-t shadow-lg",
          // Solid background to prevent content showing through
          "bg-[rgba(35,55,61,100)]",
          // Safe area padding for notched devices
          "pb-safe",
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex h-16 items-center justify-around px-2">
          {/* Dynamic actions from pages (max 3) */}
          {registeredActions.slice(0, 3).map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.onPress}
                disabled={action.disabled}
                className={cn(
                  // Size for touch target
                  "flex flex-col items-center justify-center",
                  "h-full min-w-[64px] py-2",
                  // Transition
                  "transition-all duration-200",
                  // Variant styles
                  action.variant === "primary"
                    ? "text-primary"
                    : action.variant === "danger"
                      ? "text-destructive"
                      : "text-muted-foreground hover:text-foreground",
                  "active:scale-95",
                  action.disabled && "opacity-50 cursor-not-allowed",
                )}
                aria-label={action.label}
              >
                <div className="relative">
                  <Icon className="h-6 w-6" />
                  {/* Badge */}
                  {mounted && action.badge !== undefined && action.badge > 0 && (
                    <span
                      suppressHydrationWarning
                      className={cn(
                        "absolute -right-1.5 -top-1.5",
                        "h-[18px] min-w-[18px] px-1",
                        "rounded-full",
                        "bg-red-500 text-white",
                        "text-[10px] font-bold",
                        "flex items-center justify-center",
                      )}
                    >
                      {action.badge > 99 ? "99+" : action.badge}
                    </span>
                  )}
                </div>
                <span className="mt-1 text-[12px] font-medium leading-none">
                  {action.label}
                </span>
              </button>
            );
          })}

          {/* Static navigation items */}
          {navItems.slice(0, Math.max(0, 5 - registeredActions.length)).map((item) => {
            const Icon = item.icon;
            const active = item.href !== "#" && isActive(item.href);

            // Handle action items (not links)
            if (item.action) {
              return (
                <button
                  key={item.title}
                  onClick={item.action}
                  data-tour={item.tourId}
                  className={cn(
                    // Size for touch target
                    "flex flex-col items-center justify-center",
                    "h-full min-w-[64px] py-2",
                    // Transition
                    "transition-all duration-200",
                    // Active/hover states
                    "text-muted-foreground",
                    "hover:text-foreground",
                    "active:scale-95",
                  )}
                  aria-label={item.title}
                >
                  <div className="relative">
                    <Icon
                      className={cn(
                        "h-6 w-6 transition-colors",
                        "text-muted-foreground",
                      )}
                    />
                    {/* Badge - only render after mount to prevent hydration mismatch */}
                    {mounted && item.badge !== undefined && item.badge > 0 && (
                      <span
                        suppressHydrationWarning
                        className={cn(
                          "absolute -right-1.5 -top-1.5",
                          "h-[18px] min-w-[18px] px-1",
                          "rounded-full",
                          "bg-red-500 text-white",
                          "text-[10px] font-bold",
                          "flex items-center justify-center",
                        )}
                      >
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="mt-1 text-[12px] font-medium leading-none">
                    {item.title}
                  </span>
                </button>
              );
            }

            // Regular link items
            return (
              <Link
                key={item.title}
                href={item.href}
                data-tour={item.tourId}
                onClick={item.title === "Home" ? handleHomeClick : undefined}
                className={cn(
                  // Size for touch target
                  "flex flex-col items-center justify-center",
                  "h-full min-w-[64px] py-2",
                  // Transition
                  "transition-all duration-200",
                  // Active/hover states
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                  "active:scale-95",
                )}
                aria-current={active ? "page" : undefined}
              >
                <div className="relative">
                  <Icon
                    className={cn(
                      "h-6 w-6 transition-colors",
                      active ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                  {/* Active indicator dot */}
                  {active && (
                    <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />
                  )}
                </div>
                <span
                  className={cn(
                    "mt-1 text-[12px] font-medium leading-none",
                    active && "text-primary",
                  )}
                >
                  {item.title}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* More Menu Sheet */}
      <Sheet open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
        <SheetContent
          side="bottom"
          className="mobile-more-menu-content h-auto max-h-[85vh] rounded-t-2xl lg:hidden"
        >
          <SheetTitle className="sr-only">More Options</SheetTitle>
          <SheetDescription className="sr-only">
            Additional navigation options and actions
          </SheetDescription>

          {/* Drag handle */}
          <div className="flex justify-center pb-2 pt-3">
            <div className="bg-muted-foreground/30 h-1 w-10 rounded-full" />
          </div>

          {/* User info at top */}
          {isAuthenticated && (
            <div className="mb-2 border-b px-4 pb-4">
              <p className="text-base font-semibold">
                {session?.user?.firstName || "User"}
              </p>
              <p className="text-sm text-muted-foreground">
                {session?.user?.email}
              </p>
            </div>
          )}

          <div className="space-y-2 pb-8">
            {/* Auth status dependent items */}
            {isAuthenticated ? (
              <>
                {/* Authenticated user menu items */}
                {moreMenuItems
                  .filter((item) => !item.requiresAuth || isAuthenticated)
                  .map((item) => {
                    const Icon = item.icon;
                    const active = item.href !== "#" && isActive(item.href);
                    // Add data-tour attributes for key menu items
                    const tourId =
                      item.title === "Profile"
                        ? "mobile-more-profile"
                        : item.title === "My Listings"
                          ? "mobile-more-listings"
                          : item.title === "Settings"
                            ? "mobile-more-settings"
                            : item.title === "Saved"
                              ? "mobile-more-saved"
                              : undefined;

                    // Handle action items (like Saved)
                    if (item.action) {
                      return (
                        <button
                          key={item.title}
                          onClick={() => {
                            item.action?.();
                            setMoreMenuOpen(false);
                          }}
                          data-tour={tourId}
                          className={cn(
                            "flex w-full items-center gap-4 rounded-lg px-4 py-3",
                            "transition-colors",
                            "hover:bg-accent",
                            "mobile-menu-link",
                          )}
                        >
                          <Icon
                            className={cn(
                              "h-5 w-5",
                              active ? "text-primary" : "text-muted-foreground",
                            )}
                          />
                          <span
                            className={cn(
                              "text-sm font-medium",
                              active && "text-primary",
                            )}
                          >
                            {item.title}
                          </span>
                        </button>
                      );
                    }

                    // Regular link items
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        data-tour={tourId}
                        onClick={() => setMoreMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-4 rounded-xl px-5 py-4",
                          "transition-colors",
                          "hover:bg-accent",
                          "mobile-menu-link",
                          active && "bg-accent",
                        )}
                      >
                        <Icon
                          className={cn(
                            "h-5 w-5",
                            active ? "text-primary" : "text-muted-foreground",
                          )}
                        />
                        <span
                          className={cn(
                            "text-sm font-medium",
                            active && "text-primary",
                          )}
                        >
                          {item.title}
                        </span>
                      </Link>
                    );
                  })}

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className={cn(
                    "flex w-full items-center gap-4 rounded-xl px-5 py-4",
                    "mobile-menu-link transition-colors",
                  )}
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-sm font-medium">Log Out</span>
                </button>
              </>
            ) : (
              <>
                {/* Unauthenticated user options */}
                <div className="mb-2 px-4 py-2">
                  <p className="mb-4 text-sm text-muted-foreground">
                    Sign in to access your dashboard, create listings, and
                    message other users.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        openAuthModal("signin");
                        setMoreMenuOpen(false);
                      }}
                    >
                      Log In
                    </Button>
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={() => openAuthModal("signup")}
                    >
                      Register
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Bug Report Button - BETA */}
            <div className="mt-4 border-t pt-4">
              <BugReportDialog
                trigger={
                  <button
                    data-tour="bug-report"
                    className={cn(
                      "group relative flex w-full items-center gap-4 rounded-xl px-5 py-4",
                      "bg-gradient-to-r from-red-500/10 to-orange-500/10",
                      "hover:from-red-500/20 hover:to-orange-500/20",
                      "border border-red-500/20 hover:border-red-500/40",
                      "transition-all duration-300 ease-out",
                      // Beta test highlight - pulsing animation
                      "before:absolute before:inset-0 before:rounded-lg",
                      "before:pointer-events-none before:animate-ping before:bg-red-500/20",
                      "shadow-lg shadow-red-500/20",
                    )}
                  >
                    <Bug className="h-5 w-5 shrink-0 animate-pulse text-red-500 transition-colors group-hover:text-red-400" />
                    <span className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
                      Report a Bug
                    </span>
                    {/* Beta badge */}
                    <span className="ml-auto flex animate-pulse items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-2 py-0.5 text-xs font-bold text-white">
                      BETA
                    </span>
                  </button>
                }
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
